import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { authAxios, API, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Check, X, ChevronRight, CreditCard, Banknote, Wallet, ShoppingCart, Sparkles, AlertTriangle } from "lucide-react";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = (node, lang = "es") => {
  if (node == null) return "";
  if (typeof node === "string") return node;
  return node[lang] || node.es || node.en || Object.values(node)[0] || "";
};

const PAYMENT_METHODS = [
  { id: "stripe", label: "Tarjeta (Stripe)", icon: CreditCard, description: "Pagá con tarjeta. Activación automática al aprobarse el pago." },
  { id: "mercado_pago", label: "Mercado Pago", icon: Wallet, description: "Tarjeta, Rapipago, OXXO, Pix. Activación automática al aprobarse." },
  { id: "transfer", label: "Transferencia bancaria", icon: Banknote, description: "Te mostramos los datos y activamos manualmente al recibir el pago." },
];

export default function DashboardPlans() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectSlug = searchParams.get("plan");
  const stripeSessionId = searchParams.get("session_id");
  const stripeStatusParam = searchParams.get("status");
  const mpParam = searchParams.get("mp");
  const mpRef = searchParams.get("ref");

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState("monthly");
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState("plans");
  const [payMethod, setPayMethod] = useState("stripe");
  const [transferDetails, setTransferDetails] = useState("");
  const [country, setCountry] = useState(navigator?.language?.includes("-") ? navigator.language.split("-")[1] : "AR");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pollingStatus, setPollingStatus] = useState("");
  const [myPending, setMyPending] = useState(null);
  const [providersEnabled, setProvidersEnabled] = useState({ stripe: true, mercadopago: false, paypal: false });

  useEffect(() => {
    let stop = false;
    Promise.all([
      axios.get(`${PUBLIC_API}/landing/plans/public`),
      authAxios.get(`${API}/me/subscription-status`).catch(() => ({ data: null })),
      axios.get(`${PUBLIC_API}/payments/providers/enabled`).catch(() => ({ data: { stripe: true } })),
    ]).then(([p, s, pr]) => {
      if (stop) return;
      setPlans(p.data || []);
      setMyPending(s.data?.pending || null);
      setProvidersEnabled(pr.data || { stripe: true });
      // Default pay method = first enabled provider, fallback to transfer
      const enabledOrder = ["stripe", "mercadopago", "transfer"];
      const firstEnabled = enabledOrder.find((id) => id === "transfer" || pr.data?.[id]) || "transfer";
      setPayMethod(firstEnabled);
      if (preselectSlug && p.data) {
        const match = p.data.find((pl) => pl.slug === preselectSlug);
        if (match) { setSelected(match); setStep("payment"); }
      }
    }).finally(() => !stop && setLoading(false));
    return () => { stop = true; };
  }, [preselectSlug]);

  // === Handle Stripe return ===
  useEffect(() => {
    if (!stripeSessionId) return;
    setStep("processing");
    setPollingStatus("Verificando pago con Stripe…");
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await authAxios.get(`${API}/payments/stripe/status/${stripeSessionId}`);
        if (data.payment_status === "paid") {
          setPollingStatus("");
          setStep("success");
          // Clean URL
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") {
          setError("La sesión de Stripe expiró. Probá de nuevo.");
          setStep("plans"); return;
        }
        attempts += 1;
        if (attempts < 10) setTimeout(poll, 2500);
        else { setError("No pudimos confirmar el pago. Revisá tu email."); setStep("plans"); }
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
        setStep("plans");
      }
    };
    poll();
  }, [stripeSessionId]);

  // === Handle Mercado Pago return ===
  useEffect(() => {
    if (!mpParam || !mpRef) return;
    setStep("processing");
    setPollingStatus("Esperando confirmación de Mercado Pago…");
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await authAxios.get(`${API}/payments/mercadopago/status/${mpRef}`);
        if (data.payment_status === "paid") {
          setPollingStatus("");
          setStep("success");
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.payment_status === "failed" || mpParam === "failure") {
          setError("El pago fue rechazado. Probá con otro método.");
          setStep("plans"); return;
        }
        attempts += 1;
        if (attempts < 12) setTimeout(poll, 3000);
        else { setPollingStatus("Pago en procesamiento. Te avisaremos por email cuando se acredite."); }
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
        setStep("plans");
      }
    };
    poll();
  }, [mpParam, mpRef]);

  const pickPlan = async (plan) => {
    setSelected(plan); setError(""); setStep("payment");
    if (!transferDetails) {
      try {
        const { data } = await axios.get(`${PUBLIC_API}/landing/transfer-details/public`);
        setTransferDetails(data.details || "");
      } catch { /* ignore */ }
    }
  };

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true); setError("");
    try {
      const b = selected.billing?.[billing] || {};
      const origin = window.location.origin;

      if (payMethod === "stripe") {
        const { data } = await authAxios.post(`${API}/payments/stripe/session`, {
          plan_slug: selected.slug, billing, origin,
          email: user?.email || "", name: user?.name || "", phone,
        });
        window.location.href = data.url;
        return;
      }
      if (payMethod === "mercado_pago") {
        const { data } = await authAxios.post(`${API}/payments/mercadopago/session`, {
          plan_slug: selected.slug, billing, origin, country,
          email: user?.email || "", name: user?.name || "", phone,
        });
        window.location.href = data.url;
        return;
      }
      // transfer (legacy flow, manual approval)
      await axios.post(`${PUBLIC_API}/subscriptions/request`, {
        name: user?.name || user?.email || "",
        email: user?.email || "",
        phone,
        plan_slug: selected.slug,
        plan_name: T(selected.name),
        billing_period: billing,
        price: b.price || 0,
        currency: b.currency || "USD",
        payment_method: "Transferencia bancaria",
        notes,
      });
      setStep("success");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="dashboard-plans-page">
      {/* Pending banner */}
      {myPending && step === "plans" && (
        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <ShoppingCart className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-900">
                <strong>Ya tenés una solicitud pendiente</strong> del plan <b>{myPending.plan_name}</b> ({myPending.payment_method}).
                Cuando confirmemos el pago se activa automáticamente.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: processing */}
      {step === "processing" && (
        <Card className="max-w-md mx-auto"><CardContent className="p-8 text-center space-y-3" data-testid="payment-processing">
          <Loader2 className="w-10 h-10 text-amber-500 mx-auto animate-spin" />
          <h2 className="text-xl font-semibold">Procesando pago…</h2>
          <p className="text-sm text-slate-500">{pollingStatus}</p>
        </CardContent></Card>
      )}

      {/* Step: plans */}
      {step === "plans" && (
        <>
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-7 h-7 text-amber-500" /> Elegí tu plan</h1>
            <p className="text-slate-600 mt-2">Para empezar a usar todas las funciones, activá un plan. Cancelás cuando quieras.</p>
          </div>
          {error && (
            <Card className="mb-4 border-red-300 bg-red-50"><CardContent className="p-3 flex items-center gap-2 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4" /> {error}
            </CardContent></Card>
          )}
          <div className="inline-flex gap-1 p-1 bg-slate-100 rounded-full mb-8">
            {["monthly", "yearly"].map((p) => (
              <button key={p} onClick={() => setBilling(p)} className={`px-5 py-2 rounded-full text-sm font-medium transition ${billing === p ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`billing-${p}`}>
                {p === "monthly" ? "Mensual" : "Anual (-20%)"}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((p) => {
              const b = p.billing?.[billing] || {};
              return (
                <Card key={p.id} className={`p-6 transition hover:-translate-y-1 hover:shadow-xl ${p.highlighted ? "border-2 border-amber-500 shadow-lg" : ""}`} data-testid={`plan-card-${p.slug}`}>
                  {p.highlighted && <p className="text-xs uppercase font-bold tracking-wider text-amber-600 mb-2">Más popular</p>}
                  <h3 className="text-xl font-bold" style={{ color: p.color }}>{T(p.name)}</h3>
                  <p className="text-sm text-slate-500 mt-1">{T(p.tagline)}</p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-4xl font-bold">${b.price ?? 0}</span>
                    <span className="text-sm text-slate-500">{T(b.period_label)}</span>
                  </div>
                  <ul className="mt-4 space-y-2 text-sm min-h-[180px]">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className={`flex gap-2 items-start ${f.included ? "" : "text-slate-400"}`}>
                        {f.included ? <Check className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" /> : <X className="w-4 h-4 mt-0.5 opacity-40 shrink-0" />}
                        <span className={f.included ? "" : "line-through"}>{T(f.label)}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => pickPlan(p)} className={`w-full mt-5 ${p.highlighted ? "bg-amber-500 hover:bg-amber-600 text-black" : ""}`} data-testid={`pick-${p.slug}`}>
                    {T(p.cta_label) || "Elegir"} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Step: payment */}
      {step === "payment" && selected && (
        <div className="max-w-2xl">
          <button onClick={() => setStep("plans")} className="text-sm text-slate-500 hover:underline mb-3" data-testid="back-to-plans">← Volver a los planes</button>
          <Card className="mb-4 border-2 border-amber-200 bg-amber-50/30">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-bold tracking-wider text-amber-600">Plan seleccionado</p>
                <p className="text-xl font-bold" style={{ color: selected.color }}>{T(selected.name)}</p>
                <p className="text-sm text-slate-500">{billing === "yearly" ? "Anual" : "Mensual"} · ${selected.billing?.[billing]?.price ?? 0} {selected.billing?.[billing]?.currency || ""}</p>
              </div>
              <ShoppingCart className="w-10 h-10 text-amber-500" />
            </CardContent>
          </Card>

          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Elegí cómo pagar</h2>
          <div className="space-y-2 mb-6">
            {PAYMENT_METHODS.filter((m) => {
              if (m.id === "transfer") return true;
              if (m.id === "stripe") return providersEnabled.stripe;
              if (m.id === "mercado_pago") return providersEnabled.mercadopago;
              return false;
            }).map((m) => {
              const Icon = m.icon;
              const checked = payMethod === m.id;
              return (
                <button key={m.id} onClick={() => setPayMethod(m.id)} className={`w-full text-left border rounded-lg p-4 flex items-center gap-3 transition ${checked ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200" : "border-slate-200 hover:border-slate-300"}`} data-testid={`pay-method-${m.id}`}>
                  <Icon className={`w-5 h-5 ${checked ? "text-amber-600" : "text-slate-400"}`} />
                  <div className="flex-1">
                    <p className="font-medium">{m.label}</p>
                    <p className="text-xs text-slate-500">{m.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${checked ? "border-amber-500 bg-amber-500" : "border-slate-300"}`}>
                    {checked && <Check className="w-3 h-3 text-white m-0.5" />}
                  </div>
                </button>
              );
            })}
          </div>

          {payMethod === "mercado_pago" && (
            <div className="mb-4">
              <Label>País (define la moneda)</Label>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full mt-1 rounded border border-slate-200 p-2 text-sm" data-testid="mp-country">
                <option value="AR">Argentina (ARS)</option>
                <option value="BR">Brasil (BRL)</option>
                <option value="MX">México (MXN)</option>
                <option value="CL">Chile (CLP)</option>
                <option value="CO">Colombia (COP)</option>
                <option value="PE">Perú (PEN)</option>
                <option value="UY">Uruguay (UYU)</option>
              </select>
            </div>
          )}

          {payMethod === "transfer" && (
            <Card className="mb-4 bg-slate-50">
              <CardContent className="p-4">
                <p className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">Datos para transferir</p>
                {transferDetails ? (
                  <pre className="text-xs whitespace-pre-wrap font-mono">{transferDetails}</pre>
                ) : (
                  <p className="text-xs text-slate-600 italic">Te enviamos los datos por email apenas confirmes la solicitud.</p>
                )}
                <p className="text-xs text-slate-500 mt-3">Una vez recibido el pago, activamos tu cuenta.</p>
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            <div>
              <Label>WhatsApp (opcional)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+54 9 ..." data-testid="payment-phone" />
            </div>
            {payMethod === "transfer" && (
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Cualquier comentario para el equipo" data-testid="payment-notes" />
              </div>
            )}
            {error && <p className="text-sm text-red-500" data-testid="payment-error">⚠️ {error}</p>}
            <Button onClick={submit} disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-600 text-black h-12 text-base" data-testid="submit-payment">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
              {payMethod === "transfer" ? "Confirmar solicitud" : `Pagar con ${PAYMENT_METHODS.find(p => p.id === payMethod)?.label}`}
            </Button>
          </div>
        </div>
      )}

      {/* Step: success */}
      {step === "success" && (
        <div className="max-w-md mx-auto text-center py-12" data-testid="payment-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">¡Listo!</h2>
          <p className="text-slate-600">Tu pago fue procesado. Activamos tu plan y te enviamos un email con el detalle.</p>
          <Button className="mt-6" onClick={() => { setStep("plans"); setSelected(null); window.location.href = "/dashboard"; }}>Ir al Dashboard</Button>
        </div>
      )}
    </div>
  );
}
